import { tmxApiCall } from './index'
import { RegexConstants } from '../constants.js'

// Utilities
import Gps from '../utility/gpsUtil'
import Logger from '../utility/loggerUtil'

export default {
  async verifyPaymentLocation (longitude, latitude, acctState, loanHeader, loanId, lmsId, storeId, transactionType, recordOnly = false) {
    Logger.info('Starting Verify Payment Location query.')
    return tmxApiCall('get', '/refiallowedstatus', {
      params: {
        latitude,
        longitude,
        acctState,
        loanHeader,
        loanId,
        lmsId,
        recordOnly,
        storeId,
        transactionType
      }
    })
  },

  async recordPaymentLocation (longitude, latitude, acctState, loanHeader, loanId, lmsId, storeId, transactionType, recordOnly = true) {
    Logger.info('Starting Record Payment Location query.')
    return tmxApiCall('get', '/refiallowedstatus', {
      params: {
        latitude,
        longitude,
        acctState,
        loanHeader,
        loanId,
        lmsId,
        recordOnly,
        storeId,
        transactionType
      }
    })
  },

  async saveRestrictedStateAcknowledgement (loanId, timeStamp, didConsent, lmsId, loanState, storeId, transactionType) {
    Logger.info('Starting Save Restricted State Acknowledgement query.')
    return tmxApiCall('post', '/refiallowedstatus', {
      loanId,
      timeStamp,
      consent: didConsent,
      lmsId,
      loanState,
      storeId,
      transactionType
    })
  },

  /**
   * Saves customers agreement or decline of making an additional payment when they already have an ACH payment scheduled.
   * @param {String} loanId - The identifier for a loan.
   * @param {Boolean} userConsented - True if user agrees to make additional payment, false if they decline.
   * @returns {Promise<{success: Boolean}>} True if consent was saved.
   */
  async saveAchAdditionalPaymentConsent (loanId, userConsented) {
    Logger.info('Starting Save Restricted State Acknowledgement query.')
    return tmxApiCall('post', '/addPayConsent', {
      consent: userConsented,
      loanId
    })
  },

  /**
   * Posts to /payment endpoint with payment details.
   * @param {Object} paymentInfo - The object containing the paymentInfo for the loan.
   * @returns {Promise<{messageKey: String}>} The key to check the payment response status.
   */
  async postPayment (paymentInfo) {
    Logger.info('Started post loan payment.')
    const payload = getPaymentFormattedPayload(paymentInfo)
    return tmxApiCall('post', '/payment', payload)
  },

  /**
   * Gets the payment response from /payment.
   * @param {String} messageKey - The key associated with the payment.
   * @returns {Promise<{Error: String, confirmationNumber: String, newLoanId: String, newLoanDueDate: String, newPrincipalBalance: Number}>} Payment result information.
   */
  async getPaymentStatus (messageKey) {
    Logger.info('Started Check Pay Loan status query.')
    return tmxApiCall('get', '/payment', {
      params: {
        messageKey
      }
    })
  },

  /**
 * Checks for disbursement reference details (currently only used for push to debit and MoneyGram).
 * @param {String|Number} loanHeaderId - The header identifier for a Vergent loan.
 * @param {String|Number} productId - The loan model identifier for a Vergent loan.
 * @param {String|Number} disbursementType - Indicates the type of disbursement.
 * @param {String} applicationId - Indicates the type of disbursement.
 * @returns {Promise<{success: Boolean, referenceNumber: String, email: String}>} Return data object.
 */
  async getReferenceNumber (loanHeaderId, productId, disbursementType, applicationId = undefined) {
    Logger.info('Checking for disbursement reference number')
    return tmxApiCall('get', '/disbursementReference', {
      params: {
        applicationId,
        disbursementType,
        loanHeaderId,
        productId
      }
    })
  },

  /**
   * Sets the payment timeout flag so customers will be notified when refi docs are ready.
   * @param {String} loanId - The identifier for a Vergent loan.
   * @param {Number} paymentAmount - The amount of the payment.
   * @returns {Promise<{success: Boolean}>} Returns boolean for success or failure.
   */
  async setPaymentTimeout (loanId, paymentAmount) {
    Logger.info('Setting Payment Timeout')
    return tmxApiCall('post', '/paymentTimeout', {
      loanId,
      paymentAmount
    })
  },

  /**
   * Gets demographic reference information.
   * @param {Number} productId - The config identifier for a product.
   * @param {String} customerId - The identifier for a customer.
   * @param {String} loanHeader - The header identifier for a loan.
   * @param {String} storeId - The identifier for a store.
   * @returns {Promise<{demographicReference: {genderOptions: {id: Number, Name: String}[], heightOptions: {minFeet: Number, maxFeet: Number}, demographicOptions: {gender: Boolean, height: Boolean, race: Boolean, weight: Boolean}, raceOptions: {id: Number, Name: String}[]}}>}
   */
  async getDemographicReferences (productId, customerId, loanHeader, storeId) {
    Logger.info('Started Get Demographic References')
    return tmxApiCall('get', '/demographicReference', {
      params: {
        productId,
        customerId,
        loanHeader,
        storeId
      }
    })
  },

  /**
   * Checks if the user is locked from doing add on loans; it will not lock the user.
   * @returns {Promise<{isLocked: Boolean}>} Object with isLocked property.
   */
  async isAddOnLocked () {
    Logger.info('Started check for add on lock.')
    return tmxApiCall('get', '/addOnLock')
  },

  /**
   * Checks if the user is locked from doing add on loans; has the possibility of locking the user.
   * @param {String} loanHeader - The header identifier for a loan.
   * @param {String} storeId - The identifier for a store.
   * @param {String} [latitude] - The X coordinate of the gps position of the customer.
   * @param {String} [longitude] - The Y coordinate of the gps position of the customer.
   * @returns {Promise<{isLocked: Boolean}>} Object with isLocked property.
   */
  async tryAddOnLock (loanHeader, storeId, latitude, longitude) {
    Logger.info('Started try/check for add on lock.')
    return tmxApiCall('post', '/addOnLock', {
      loanHeader,
      storeId,
      latitude,
      longitude
    })
  },

  /**
   * Posts to /canRefinance endpoint with as many payment details as we have.
   * @param {Object} paymentInfo - The object containing the paymentInfo for the loan.
   * @returns {Promise<{canRefi: Boolean}>} - Whether this loan is refi-able based on the current information.
   */
  async canRefinance (paymentInfo) {
    Logger.info('Started post loan payment.')
    const payload = getPaymentFormattedPayload(paymentInfo)
    return tmxApiCall('post', '/canRefinance', payload)
  }
}

const getPaymentFormattedPayload = (paymentInfo) => {
  return {
    confirmationDetails: {
      email: paymentInfo.email,
      phoneNumber: paymentInfo.phoneNumber,
      cardHolderName: paymentInfo.cardHolderName,
      loanId: paymentInfo.loanId,
      loanHeader: paymentInfo.loanHeader,
      amountPaid: Number.parseFloat(paymentInfo.paymentAmount).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,'),
      cardNumber: paymentInfo.cardNumber.slice(-4),
      customerName: paymentInfo.customerName,
      storePhoneNumber: paymentInfo.storePhoneNumber,
      productBrand: paymentInfo.productBrand,
      collateralYear: paymentInfo.collateralYear,
      collateralMake: paymentInfo.collateralMake,
      collateralModel: paymentInfo.collateralModel,
      isPayoff: paymentInfo.isPayoff === true,
      isPartial: paymentInfo.isPartial === true,
      paymentDueDate: paymentInfo.paymentDueDate,
      username: paymentInfo.username,
      storeId: paymentInfo.storeId
    },
    storeId: paymentInfo.storeId,
    loanId: paymentInfo.loanId,
    loanHeader: paymentInfo.loanHeader,
    paymentAmount: paymentInfo.paymentAmount,
    paymentCardToken: paymentInfo.paymentCardToken,
    cardIds: paymentInfo.paymentCardIds,
    disburseCardIds: paymentInfo.disburseCardIds,
    isRefi: paymentInfo.isRefi,
    autoPay: paymentInfo.autoPay,
    disbursementAmount: paymentInfo.disbursementAmount,
    disbursementType: paymentInfo.disbursementType,
    lmsId: paymentInfo.lmsId,
    intentId: paymentInfo.intentId,
    migrateProduct: paymentInfo.migrateProduct,
    ccv: paymentInfo.cvv,
    timeZone: Gps.getTimezone(),
    paymentType: paymentInfo.paymentType,
    guestPayment: paymentInfo.guestPayment,
    riskCheck: paymentInfo.riskCheck,
    ofrPaymentValue: paymentInfo.ofrPaymentValue,
    discountAmount: paymentInfo.discountAmount
  }
}
